import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { getMySubmissions, withdrawSubmission } from '../services/dataService';
import { useNotification } from '../components/framework/NotificationContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import RecordDetailView from '../components/admin/RecordDetailView';
import { polymersText, solventsText } from '../components/DataSubmission/solutionText';
import '../styles/submission/contribute.css';
import '../styles/submission/my-contributions.css';

// A run's status, in the contributor's words
const STATUS = {
    NEEDS_UPDATE: { label: 'Needs update', tone: 'warning', icon: 'bi-exclamation-circle' },
    REJECTED: { label: 'Rejected', tone: 'danger', icon: 'bi-x-circle' },
    PENDING: { label: 'In review', tone: 'neutral', icon: 'bi-hourglass-split' },
    APPROVED: { label: 'Published', tone: 'success', icon: 'bi-check-circle' },
};
const ORDER = ['NEEDS_UPDATE', 'REJECTED', 'PENDING', 'APPROVED'];

const FILTERS = [
    { key: 'ALL', label: 'All' },
    { key: 'ACTION', label: 'Needs your action', statuses: ['NEEDS_UPDATE', 'REJECTED'] },
    { key: 'PENDING', label: 'In review', statuses: ['PENDING'] },
    { key: 'APPROVED', label: 'Published', statuses: ['APPROVED'] },
];

const runStatus = (run) => (STATUS[(run.status || 'PENDING').toUpperCase()] ? (run.status || 'PENDING').toUpperCase() : 'PENDING');

const countByStatus = (runs) => runs.reduce((acc, r) => {
    const s = runStatus(r);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
}, {});

const withUnit = (value, unit) => (value === null || value === undefined || value === '' ? '' : `${value}${unit ? ` ${unit}` : ''}`);

const doiHref = (doi) => (!doi ? null : /^https?:\/\//i.test(doi) ? doi : `https://doi.org/${doi.replace(/^doi:\s*/i, '')}`);

const formatDate = (value) => {
    const d = value ? new Date(value) : null;
    return d && !isNaN(d) ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
};

const runPolymers = (run) => {
    const feeds = run.solutions || [];
    if (feeds.length > 1 || feeds.some(f => f.preparation === 'MIXED')) {
        return [...new Set((run.polymerProperty?.polymerComponents || []).map(c => c.polymerName).filter(Boolean))].join(' + ');
    }
    return polymersText(run.polymerProperty?.polymerComponents);
};

function StatusChip({ status, count }) {
    const meta = STATUS[status];
    return (
        <span className={`cx-runstatus cx-runstatus--${meta.tone}`}>
            <i className={`bi ${meta.icon}`} aria-hidden="true"></i>
            <span className="cx-runstatus__label">{count !== undefined ? `${count} ` : ''}{meta.label.toLowerCase()}</span>
        </span>
    );
}

/** The share of runs per status, as one bar. */
function StatusBar({ counts, total }) {
    return (
        <div className="mc-bar" role="img" aria-label={ORDER.filter(s => counts[s]).map(s => `${counts[s]} ${STATUS[s].label.toLowerCase()}`).join(', ')}>
            {ORDER.filter(s => counts[s]).map(s => (
                <span key={s} className={`mc-bar__seg mc-bar__seg--${STATUS[s].tone}`} style={{ flexGrow: counts[s] / total }} />
            ))}
        </div>
    );
}

export default function MySubmissionsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const confirm = useConfirmation();
    const { success, error: notifyError } = useNotification();

    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [filter, setFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(() => new Set());
    const [viewRun, setViewRun] = useState(null);
    const [saved, setSaved] = useState(location.state?.saved || null);

    const load = async () => {
        try {
            setLoading(true);
            const data = await getMySubmissions();
            setSubmissions((data || []).sort((a, b) => new Date(b.submissionTimestamp) - new Date(a.submissionTimestamp)));
            setLoadError(null);
        } catch (e) {
            console.error(e);
            setLoadError('Your contributions could not be loaded. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // The edit page hands over what it saved; open that contribution and forget the
    // state so a reload does not show the message again
    useEffect(() => {
        if (location.state?.saved) {
            setExpanded(new Set([location.state.saved.submissionId]));
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    const allRuns = useMemo(() => submissions.flatMap(s => s.experimentData || []), [submissions]);
    const totals = useMemo(() => countByStatus(allRuns), [allRuns]);
    const actionCount = (totals.NEEDS_UPDATE || 0) + (totals.REJECTED || 0);

    const filterCount = (f) => (f.statuses ? f.statuses.reduce((n, s) => n + (totals[s] || 0), 0) : allRuns.length);

    const visible = useMemo(() => {
        const f = FILTERS.find(x => x.key === filter);
        const q = search.trim().toLowerCase();
        return submissions.filter(sub => {
            const runs = sub.experimentData || [];
            if (f?.statuses && !runs.some(r => f.statuses.includes(runStatus(r)))) return false;
            if (!q) return true;
            const rm = sub.researchMetadata || {};
            const haystack = [`#${sub.submissionId}`, rm.publicationTitle, rm.doi, rm.deviceModel,
                ...runs.flatMap(r => (r.polymerProperty?.polymerComponents || []).map(c => c.polymerName))]
                .filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
    }, [submissions, filter, search]);

    const toggle = (id) => setExpanded(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const handleWithdraw = async (sub) => {
        const runs = sub.experimentData || [];
        const removable = runs.filter(r => runStatus(r) !== 'APPROVED').length;
        const published = runs.length - removable;
        const ok = await confirm({
            title: `Withdraw contribution #${sub.submissionId}?`,
            message: `This deletes ${removable} unpublished run${removable === 1 ? '' : 's'} and their images. `
                + (published ? `The ${published} published run${published === 1 ? '' : 's'} stay${published === 1 ? 's' : ''} in the dataset. ` : '')
                + 'This cannot be undone.',
            confirmLabel: 'Withdraw',
            type: 'danger',
        });
        if (!ok) return;
        try {
            await withdrawSubmission(sub.submissionId);
            success(`Contribution #${sub.submissionId} was withdrawn.`);
            load();
        } catch (e) {
            console.error(e);
            notifyError('The contribution could not be withdrawn. Please try again.');
        }
    };

    const savedSummary = () => {
        if (!saved?.result?.records) return null;
        const records = saved.result.records;
        const counts = countByStatus(records);
        const removed = saved.result.removedRecords?.length || 0;
        const parts = [];
        if (counts.PENDING) parts.push(`${counts.PENDING} run${counts.PENDING === 1 ? '' : 's'} in review`);
        if (counts.NEEDS_UPDATE) parts.push(`${counts.NEEDS_UPDATE} still need${counts.NEEDS_UPDATE === 1 ? 's' : ''} an update`);
        if (counts.REJECTED) parts.push(`${counts.REJECTED} rejected, unchanged`);
        if (counts.APPROVED) parts.push(`${counts.APPROVED} published, unchanged`);
        if (removed) parts.push(`${removed} deleted`);
        return { text: parts.join(' · '), warn: !!counts.NEEDS_UPDATE };
    };

    if (loading) {
        return (
            <div className="cx"><div className="cx-container cx-container--wide text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>Loading your contributions…
            </div></div>
        );
    }

    const summary = savedSummary();

    return (
        <div className="cx">
            <div className="cx-container cx-container--wide">
                <header className="cx-pagehead">
                    <div>
                        <h1>My contributions</h1>
                        <p>Follow the review of your runs, answer the moderators' feedback and correct what you submitted.</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <Link to="/bulk-upload" className="cx-btn cx-btn--secondary"><i className="bi bi-file-earmark-spreadsheet" aria-hidden="true"></i> Bulk upload</Link>
                        <Link to="/electrospinningTable" className="cx-btn cx-btn--primary"><i className="bi bi-plus-lg" aria-hidden="true"></i> New contribution</Link>
                    </div>
                </header>

                {loadError && (
                    <div className="cx-notice cx-notice--danger" role="alert">
                        <span><i className="bi bi-exclamation-triangle me-2"></i>{loadError}</span>
                        <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={load}>Try again</button>
                    </div>
                )}

                {summary && (
                    <div className={`cx-notice ${summary.warn ? 'cx-notice--warning' : 'cx-notice--success'}`} role="status">
                        <span><i className={`bi ${summary.warn ? 'bi-exclamation-circle' : 'bi-check-circle'} me-2`}></i>
                            <strong>Contribution #{saved.submissionId} saved.</strong> {summary.text}.</span>
                        <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setSaved(null)}></button>
                    </div>
                )}

                {submissions.length === 0 && !loadError ? (
                    <section className="cx-card mc-empty">
                        <i className="bi bi-inboxes" aria-hidden="true"></i>
                        <h2>No contributions yet</h2>
                        <p>Runs you contribute appear here with their review status. Add them one publication at a time in the web form, or many at once from a spreadsheet.</p>
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                            <Link to="/electrospinningTable" className="cx-btn cx-btn--primary">Open the web form</Link>
                            <Link to="/bulk-upload" className="cx-btn cx-btn--secondary">Bulk upload</Link>
                        </div>
                    </section>
                ) : submissions.length > 0 && (
                    <>
                        {actionCount > 0 && filter !== 'ACTION' && (
                            <button type="button" className="mc-callout" onClick={() => setFilter('ACTION')}>
                                <i className="bi bi-exclamation-circle" aria-hidden="true"></i>
                                <span><strong>{actionCount} run{actionCount === 1 ? '' : 's'} need{actionCount === 1 ? 's' : ''} your action.</strong> See what the moderators asked for and correct them.</span>
                                <i className="bi bi-chevron-right ms-auto" aria-hidden="true"></i>
                            </button>
                        )}

                        <div className="mc-toolbar">
                            <div className="mc-tabs" role="tablist" aria-label="Filter by status">
                                {FILTERS.map(f => (
                                    <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                                        className={`mc-tab ${filter === f.key ? 'is-active' : ''} ${f.key === 'ACTION' && actionCount ? 'mc-tab--alert' : ''}`}
                                        onClick={() => setFilter(f.key)}>
                                        {f.label}<span className="mc-tab__count">{filterCount(f)}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="mc-search">
                                <i className="bi bi-search" aria-hidden="true"></i>
                                <input type="search" className="form-control form-control-sm" placeholder="Search title, DOI, polymer or #id"
                                    aria-label="Search contributions" value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <p className="cx-hint mb-2">Counts are runs; {submissions.length} contribution{submissions.length === 1 ? '' : 's'} in total.</p>

                        {visible.length === 0 ? (
                            <section className="cx-card mc-empty mc-empty--small">
                                <p className="mb-2">No contribution matches {search ? <>“{search}”</> : 'this filter'}.</p>
                                <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={() => { setSearch(''); setFilter('ALL'); }}>Show all</button>
                            </section>
                        ) : (
                            <ul className="mc-list">
                                {visible.map(sub => (
                                    <ContributionCard key={sub.submissionId} sub={sub}
                                        open={expanded.has(sub.submissionId)} onToggle={() => toggle(sub.submissionId)}
                                        onEdit={() => navigate(`/edit-submission/${sub.submissionId}`)}
                                        onWithdraw={() => handleWithdraw(sub)} onViewRun={setViewRun} />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            <Modal show={!!viewRun} onHide={() => setViewRun(null)} size="xl" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title as="h5">Run #{viewRun?.recordId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{viewRun && <RecordDetailView experiment={viewRun} />}</Modal.Body>
            </Modal>
        </div>
    );
}

function ContributionCard({ sub, open, onToggle, onEdit, onWithdraw, onViewRun }) {
    const runs = sub.experimentData || [];
    const counts = countByStatus(runs);
    const rm = sub.researchMetadata || {};
    const action = (counts.NEEDS_UPDATE || 0) + (counts.REJECTED || 0);
    const editable = runs.some(r => runStatus(r) !== 'APPROVED');
    const withNotes = runs.filter(r => r.reviewNote || r.openIssues?.length);
    const polymers = [...new Set(runs.flatMap(r => (r.polymerProperty?.polymerComponents || []).map(c => c.polymerName)).filter(Boolean))];
    const href = doiHref(rm.doi);
    const detailsId = `mc-runs-${sub.submissionId}`;

    return (
        <li className={`cx-card mc-card ${action ? 'mc-card--action' : ''}`}>
            <div className="mc-card__main">
                <div className="mc-card__text">
                    <h2 className="mc-card__title">{rm.publicationTitle || (rm.doi ? `Publication ${rm.doi}` : 'Untitled publication')}</h2>
                    <div className="mc-card__meta">
                        <span>#{sub.submissionId}</span>
                        <span>{formatDate(sub.submissionTimestamp)}</span>
                        {href && <a href={href} target="_blank" rel="noopener noreferrer" className="text-break">{rm.doi}</a>}
                        {sub.imageOnly && <span className="cx-badge">Fiber images only</span>}
                    </div>
                    {polymers.length > 0 && <div className="mc-card__polymers">{polymers.slice(0, 6).join(' · ')}{polymers.length > 6 ? ` · +${polymers.length - 6}` : ''}</div>}
                </div>
                <div className="mc-card__actions">
                    {action > 0 ? (
                        <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" onClick={onEdit}>
                            <i className="bi bi-pencil" aria-hidden="true"></i> Fix {action} run{action === 1 ? '' : 's'}
                        </button>
                    ) : editable && (
                        <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={onEdit}>
                            <i className="bi bi-pencil" aria-hidden="true"></i> Edit
                        </button>
                    )}
                    {editable && (
                        <button type="button" className="cx-btn cx-btn--danger-ghost cx-btn--sm" onClick={onWithdraw}
                            title="Delete the runs that are not published">
                            <i className="bi bi-trash3" aria-hidden="true"></i> Withdraw
                        </button>
                    )}
                </div>
            </div>

            <div className="mc-card__status">
                <StatusBar counts={counts} total={runs.length || 1} />
                <div className="mc-card__chips">
                    <span className="mc-card__runs">{runs.length} run{runs.length === 1 ? '' : 's'}</span>
                    {ORDER.filter(s => counts[s]).map(s => <StatusChip key={s} status={s} count={counts[s]} />)}
                </div>
            </div>

            {!open && withNotes.length > 0 && (
                <p className="mc-card__note">
                    <i className="bi bi-chat-left-text me-2" aria-hidden="true"></i>
                    {withNotes[0].reviewNote || withNotes[0].openIssues[0]}
                    {withNotes.length > 1 || (withNotes[0].openIssues?.length || 0) + (withNotes[0].reviewNote ? 1 : 0) > 1 ? ' …' : ''}
                </p>
            )}

            <button type="button" className="mc-card__toggle" aria-expanded={open} aria-controls={detailsId} onClick={onToggle}>
                <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true"></i>
                {open ? 'Hide runs' : 'Show runs'}
            </button>

            {open && (
                <div id={detailsId} className="mc-runs">
                    <table className="mc-runs__table">
                        <thead>
                            <tr>
                                <th scope="col">Run</th>
                                <th scope="col">Status</th>
                                <th scope="col">Polymer(s)</th>
                                <th scope="col">Solvent(s)</th>
                                <th scope="col">Voltage</th>
                                <th scope="col">Flow rate</th>
                                <th scope="col">Distance</th>
                                <th scope="col">Fiber diameter</th>
                                <th scope="col"><span className="visually-hidden">Details</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...runs].sort((a, b) => ORDER.indexOf(runStatus(a)) - ORDER.indexOf(runStatus(b)) || a.recordId - b.recordId).map(run => {
                                const pp = run.processParameter || {};
                                const fp = run.fiberProperty || {};
                                const feedback = [run.reviewNote && { note: true, text: run.reviewNote }, ...(run.openIssues || []).map(text => ({ text }))].filter(Boolean);
                                return (
                                    <React.Fragment key={run.recordId}>
                                        <tr>
                                            <td data-label="Run">#{run.recordId}</td>
                                            <td data-label="Status"><StatusChip status={runStatus(run)} /></td>
                                            <td data-label="Polymer(s)">{runPolymers(run) || '–'}</td>
                                            <td data-label="Solvent(s)">{solventsText(run.solventProperty?.solventComponents) || '–'}</td>
                                            <td data-label="Voltage">{withUnit(pp.voltage, pp.voltageUnit) || '–'}</td>
                                            <td data-label="Flow rate">{withUnit(pp.flowRate, pp.flowRateUnit) || '–'}</td>
                                            <td data-label="Distance">{withUnit(pp.tipCollectorDistance, pp.tipCollectorDistanceUnit) || '–'}</td>
                                            <td data-label="Fiber diameter">{withUnit(fp.fiberDiameter, fp.fiberDiameterUnit) || '–'}</td>
                                            <td className="text-end">
                                                <button type="button" className="cx-btn cx-btn--ghost cx-btn--sm" onClick={() => onViewRun({ ...run, researchMetadata: run.researchMetadata || rm })}>View</button>
                                            </td>
                                        </tr>
                                        {feedback.length > 0 && (
                                            <tr className="mc-runs__feedback">
                                                <td colSpan={9}>
                                                    {feedback.map((f, i) => (
                                                        <div key={i} className={f.note ? 'mc-runs__note' : 'mc-runs__issue'}>
                                                            <i className={`bi ${f.note ? 'bi-chat-left-text' : 'bi-dot'} me-1`} aria-hidden="true"></i>
                                                            {f.note && <strong>Moderator{run.reviewedAt ? `, ${formatDate(run.reviewedAt)}` : ''}: </strong>}
                                                            {f.text}
                                                        </div>
                                                    ))}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </li>
    );
}
